import { ChangeDetectionStrategy, Component, computed, effect, inject, linkedSignal, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { StateService } from "../../services/state.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { DropDownPaginatedModel } from "../../pojo/DropDownPaginatedModel";
import { S25DropdownPaginatedModule } from "../../modules/s25-dropdown/s25.dropdown.paginated.module";
import { S25DropdownPaginatedComponent } from "../../modules/s25-dropdown/s25.dropdown.paginated.component";
import { S25ItemUnwrapperComponent } from "../../modules/s25-item/s25.item.unwrapper.component";
import { RecentlyViewedStore } from "../../stores/recently.viewed.store";

@TypeManagerDecorator("s25-recently-viewed")
@Component({
    selector: "s25-recently-viewed",
    template: `
        @if (hasItems()) {
            <s25-dropdown-paginated
                [model]="model"
                [(chosen)]="chosen"
                [onSelect]="onSelect"
                [choice]="choiceTmpl"
                [ariaLabel]="'Recently Viewed'"
            />
        } @else {
            <div class="c-recentlyViewedEmpty c-input c-input-disabled">
                <span>Nothing recently viewed</span>
                <s25-ng-icon [type]="'arrowSelect'" [inline]="true" [ariaLabel]="false" />
            </div>
        }
        <ng-template #choiceTmpl let-item="item">
            <s25-item-unwrapper [modelBean]="item" [includeTypeIcon]="true" />
        </ng-template>
    `,
    standalone: true,
    imports: [S25DropdownPaginatedModule, S25ItemUnwrapperComponent, S25IconComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: `
        :host {
            display: block;
            min-width: 15rem;

            @media screen and (max-width: 700px) {
                min-width: 10rem;
            }
        }

        .c-recentlyViewedEmpty {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 0.5em 0 1em;
            font-size: 0.75rem;
            line-height: 1.5rem;
            color: var(--color--text-muted);

            s25-ng-icon {
                color: var(--color--form-border);
            }
        }
    `,
})
export class RecentlyViewed {
    private store = inject(RecentlyViewedStore);

    readonly items = this.store.items;
    readonly hasItems = computed(() => this.items()?.length);

    readonly chosen = linkedSignal({
        source: this.items,
        computation: (): DropDownItem | undefined => undefined,
    });

    private dropdown = viewChild(S25DropdownPaginatedComponent);

    model: DropDownPaginatedModel = {
        items: [],
        itemNameProp: "itemName",
        emptyText: "",
        placeholder: "Recently Viewed",
        noItemIdForGet: true,
    };

    readonly onSelect = (chosen?: DropDownItem) => {
        if (chosen) {
            StateService.gotoItem(chosen as any);
        }
    };

    constructor() {
        effect(() => {
            const items = this.items();
            this.model.items = items;

            const dropdown = this.dropdown();
            if (dropdown) {
                dropdown.model.items = items;
                dropdown.setupItems(items ?? []);
                dropdown.currResults = [...(items ?? [])];
                dropdown.resetArrows();
            }
        });
    }
}
